import React, { useEffect, useState } from "react";
import SearchSelect from "@ui-components/Select/GenericSelect";
import GenericInput from "@ui-components/Input/GenericInput";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import {
  CARRIER_MEDIUMS,
  carrierMediumLabel,
  type CarrierMedium,
} from "kontrolEdge/helpers/carrierAccess";
import { fetchExtenderPopCpeInterfacesAPI } from "../requests/request";
import type { ManualLegDraft } from "../extenderPopCarrierAccessData";

interface CpeInterface {
  id: number;
  name: string;
  description: string | null;
}

interface ManualLegFieldsProps {
  extenderPopId: number;
  tenantId: number;
  cpes: { id: number; serviceId: string; name: string | null }[];
  value: ManualLegDraft;
  onChange: (value: ManualLegDraft) => void;
}

/**
 * A leg declared by hand (CTX-830): the CPE, the interface of its router the
 * carrier reaches, and the carrier with its medium (CTX-800), circuit and
 * service request. The interface is what the leg's status and traffic are
 * read from.
 */
export default function ManualLegFields({
  extenderPopId,
  tenantId,
  cpes,
  value,
  onChange,
}: ManualLegFieldsProps) {
  const api = useAxiosAuth0();
  const [interfaces, setInterfaces] = useState<CpeInterface[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!value.cpeId) {
      setInterfaces([]);
      return undefined;
    }
    let cancelled = false;
    setIsLoading(true);
    fetchExtenderPopCpeInterfacesAPI(api, extenderPopId, value.cpeId, tenantId)
      .then((response) => {
        if (!cancelled) setInterfaces(response.data?.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setInterfaces([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, extenderPopId, tenantId, value.cpeId]);

  return (
    <div className="flex flex-col gap-3">
      <SearchSelect
        label="CPE"
        placeholder="Select a CPE"
        options={cpes.map((cpe) => ({
          value: String(cpe.id),
          label: cpe.name ? `${cpe.serviceId} · ${cpe.name}` : cpe.serviceId,
        }))}
        defaultValue={value.cpeId ? String(value.cpeId) : undefined}
        onSelect={(selected) =>
          onChange({ ...value, cpeId: Number(selected), portAllocationId: null })
        }
      />
      {/* Remounted per CPE so a stale interface never stays selected. */}
      <SearchSelect
        key={value.cpeId ?? "none"}
        label="Interface"
        placeholder={value.cpeId ? "Select an interface" : "Select a CPE first"}
        loading={isLoading}
        options={interfaces.map((iface) => ({
          value: String(iface.id),
          label: iface.description
            ? `${iface.name} · ${iface.description}`
            : iface.name,
        }))}
        onSelect={(selected) =>
          onChange({ ...value, portAllocationId: Number(selected) })
        }
      />
      <GenericInput
        label="Carrier code"
        placeholder="e.g. AXTEL"
        value={value.carrierCode}
        onChange={(carrierCode) =>
          onChange({ ...value, carrierCode: carrierCode.toUpperCase() })
        }
        fullWidth
      />
      <SearchSelect
        label="Type"
        placeholder={carrierMediumLabel(null)}
        options={CARRIER_MEDIUMS.map((medium) => ({
          value: medium,
          label: carrierMediumLabel(medium),
        }))}
        defaultValue={value.medium ?? undefined}
        onSelect={(selected) =>
          onChange({ ...value, medium: selected as CarrierMedium })
        }
      />
      <GenericInput
        label="Circuit ID"
        placeholder="Optional"
        value={value.circuitId}
        onChange={(circuitId) => onChange({ ...value, circuitId })}
        fullWidth
      />
      <GenericInput
        label="Service request"
        placeholder="Optional"
        value={value.serviceRequestId}
        onChange={(serviceRequestId) => onChange({ ...value, serviceRequestId })}
        fullWidth
      />
    </div>
  );
}
